import React from "react";
import { Link, NavLink } from "react-router-dom";

import { InvestmentsAccountMenu } from "@features/investments/components/account-menu";
import { useInvestmentsAccessQuery } from "@features/investments/queries";

type InvestmentsWorkspaceShellProps = {
  title: string;
  description: string;
  generatedAt?: string;
  headerMeta?: React.ReactNode;
  modeLabel?: string;
  generatedLabel?: string;
  eyebrow?: string;
  children: React.ReactNode;
};

export function InvestmentsWorkspaceShell({
  title,
  description,
  generatedAt,
  headerMeta,
  modeLabel,
  generatedLabel = "Last recomputed",
  eyebrow = "Investments",
  children,
}: InvestmentsWorkspaceShellProps): JSX.Element {
  const accessQuery = useInvestmentsAccessQuery();
  const isOperator = accessQuery.data?.isGod === true;
  const resolvedModeLabel = modeLabel ?? (isOperator ? "Operator Mode" : "Snapshot Research");

  return (
    <div className="investments-page">
      <div className="investments-shell">
        <header className="investments-header investments-header-dashboard">
          <div>
            <div className="section-eyebrow">{eyebrow}</div>
            <h1>{title}</h1>
            <p>{description}</p>
          </div>
          <div className="dashboard-meta">
            {headerMeta ?? (
              <div className="dashboard-generated-at">{generatedLabel}: {formatDateTime(generatedAt ?? "")}</div>
            )}
            <div className="investments-header-toolbar">
              <div className="investments-header-actions">
                <Link className="investments-link" to="/">Back to Home</Link>
              </div>
              <InvestmentsAccountMenu modeLabel={resolvedModeLabel} />
            </div>
          </div>
        </header>

        <InvestmentsSubnav isOperator={isOperator} />

        {children}
      </div>
    </div>
  );
}

export function InvestmentsSubnav({ isOperator }: { isOperator: boolean }): JSX.Element {
  return (
    <nav className="investments-subnav" aria-label="Investments sections">
      {isOperator && (
        <NavLink end to="/investments" className={({ isActive }) => `investments-subnav-link${isActive ? " active" : ""}`}>
          Command
        </NavLink>
      )}
      <NavLink to="/investments/coverage" className={({ isActive }) => `investments-subnav-link${isActive ? " active" : ""}`}>
        Coverage
      </NavLink>
      <NavLink to="/investments/macro" className={({ isActive }) => `investments-subnav-link${isActive ? " active" : ""}`}>
        Macro
      </NavLink>
      {isOperator && (
        <NavLink to="/investments/portfolio" className={({ isActive }) => `investments-subnav-link${isActive ? " active" : ""}`}>
          Portfolio
        </NavLink>
      )}
      <NavLink to="/investments/reference/glossary" className={({ isActive }) => `investments-subnav-link${isActive ? " active" : ""}`}>
        Reference
      </NavLink>
    </nav>
  );
}

function formatDateTime(value: string): string {
  if (!value) {
    return "N/A";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
